import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from 'react'
import uPlot from 'uplot'
import 'uplot/dist/uPlot.min.css'
import {
  HistorySeries, UK_TZ, fmtKw, fmtSoc, pointLabel, tickFormatter,
} from '../lib/history'

/**
 * Power and SoC charts for the History page (and, with `compact`, the
 * 6-hour strip on the Live view), drawn with uPlot on canvas.
 *
 * - Grid green, battery blue (SoC too), home use orange. Shades are read from
 *   CSS custom properties in styles/history.css, so a theme can override them.
 * - Drag across either chart to zoom; double-click to zoom back out. The two
 *   charts share the time axis and the cursor.
 * - The legend shows the values under the pointer, or the latest values when
 *   the pointer is elsewhere.
 * - Aggregated views shade each bucket's grid min..max and the load average..max,
 *   so short spikes stay visible after averaging.
 *
 * v1.3.1
 * - Touch zoom: drag one finger sideways across a chart to select a span
 *   (vertical swipes still scroll the page); double-tap to zoom back out.
 * - Axis labels: fixed the font. The canvas can't read CSS variables, so
 *   '12px var(--font-body)' was rejected and labels fell back to the browser's
 *   10 device-pixel default (about 3 px on a phone). The font family is now
 *   read from the page, as 12 px, or 13 px on narrow screens (< 520 px) with
 *   wider tick spacing.
 *   Rotating a phone across that width rebuilds the charts, keeping the zoom.
 * - Image export: the legend moves to its own line when it would overlap the
 *   date range (phones in portrait).
 * - A tap shows the values at that point (no reliance on the browser turning
 *   taps into mouse moves).
 * - Fix: choosing a new range now always shows that range. Before, a zoom was
 *   kept when the new range had the same resolution (e.g. Today -> Yesterday).
 *   Live updates to the same range still keep the zoom.
 *
 * v1.3.2
 * - `hold`: when the page swaps in finer data for a zoomed span, the chart
 *   keeps showing exactly that span (rebuilding if the resolution changed).
 * - `onResetZoom`: double-click, double-tap and "Reset zoom" all go through
 *   the page, which drops the detail and returns to the full range. uPlot's
 *   own double-click reset is replaced, since it would only reset to the
 *   detail's span.
 */

export interface HistoryChartsHandle {
  resetZoom(): void
  /** PNG of the visible charts under a title, for the "Download image" button. */
  toPng(title: string, subtitle: string): Promise<Blob | null>
}

interface Props {
  series: HistorySeries
  /** Power chart only, shorter, no zoom (Live-view strip). */
  compact?: boolean
  /** Called with the visible range whenever it changes (zoom, reset, new data). */
  onViewChange?: (from: number, to: number, zoomed: boolean) => void
  /** Identifies the requested range. A new key resets the zoom; live updates keep it. */
  rangeKey?: string
  /** Keep this span on screen when new data arrives (finer detail for a zoom). */
  hold?: { min: number; max: number } | null
  /** Called instead of the built-in reset when the customer zooms out. */
  onResetZoom?: () => void
}

interface Palette {
  grid: string; battery: string; load: string; axis: string; gridline: string; zero: string
  gridBand: string; loadBand: string; surface: string; text: string
}

function palette(): Palette {
  const cs = getComputedStyle(document.documentElement)
  const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback
  return {
    grid: v('--chart-grid', '#2d6a12'),
    battery: v('--chart-battery', '#2a78d6'),
    load: v('--chart-load', '#e88a24'),
    axis: v('--chart-axis', '#5f665c'),
    gridline: v('--chart-gridline', '#e9e7e1'),
    zero: v('--chart-zero', '#9a9f95'),
    gridBand: v('--chart-grid-band', 'rgba(45, 106, 18, 0.14)'),
    loadBand: v('--chart-load-band', 'rgba(232, 138, 36, 0.16)'),
    surface: v('--color-surface', '#ffffff'),
    text: v('--color-text', '#1d231c'),
  }
}

/** Canvas fonts can't use CSS variables, so resolve the page's real font family. */
function canvasFont(px: number, weight = ''): string {
  const family = getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif'
  return `${weight ? weight + ' ' : ''}${px}px ${family}`
}

const tzDate = (ts: number) => uPlot.tzDate(new Date(ts * 1e3), UK_TZ)

const NARROW_PX = 520
const isNarrow = (width: number) => width < NARROW_PX

function chartHeights(width: number, compact: boolean) {
  if (compact) return { power: width < 520 ? 140 : 160, soc: 0 }
  return width < 520 ? { power: 230, soc: 110 } : { power: 300, soc: 140 }
}

function lastIndex(s: HistorySeries, upTo?: number): number {
  for (let i = s.t.length - 1; i >= 0; i--) {
    if ((upTo == null || s.t[i] <= upTo) && s.grid[i] != null) return i
  }
  return -1
}

export const HistoryCharts = forwardRef<HistoryChartsHandle, Props>(function HistoryCharts(
  { series, compact = false, onViewChange, rangeKey, hold, onResetZoom }, ref,
) {
  const syncKey = useId()
  const powerEl = useRef<HTMLDivElement>(null)
  const socEl = useRef<HTMLDivElement>(null)
  const powerChart = useRef<uPlot | null>(null)
  const socChart = useRef<uPlot | null>(null)
  const zoomed = useRef(false)
  const syncing = useRef(false)
  const seriesRef = useRef(series)
  seriesRef.current = series
  const viewCb = useRef(onViewChange)
  viewCb.current = onViewChange
  const built = useRef<{ resolution: string; compact: boolean; narrow: boolean } | null>(null)
  const shownKey = useRef(rangeKey)
  const lastTouch = useRef(0)
  const resetCb = useRef(onResetZoom)
  resetCb.current = onResetZoom
  const detachTouch = useRef<(() => void)[]>([])

  const vGrid = useRef<HTMLElement>(null)
  const vBattery = useRef<HTMLElement>(null)
  const vLoad = useRef<HTMLElement>(null)
  const vSoc = useRef<HTMLElement>(null)
  const vWhen = useRef<HTMLElement>(null)

  const aggregated = series.resolution !== '30s'

  function showValues(idx: number | null) {
    const s = seriesRef.current
    const i = idx ?? lastIndex(s, powerChart.current?.scales.x.max ?? undefined)
    const set = (el: HTMLElement | null, text: string) => { if (el) el.textContent = text }
    if (i < 0 || i >= s.t.length) {
      set(vGrid.current, '–'); set(vBattery.current, '–'); set(vLoad.current, '–'); set(vSoc.current, '–')
      set(vWhen.current, '')
      return
    }
    set(vGrid.current, fmtKw(s.grid[i]))
    set(vBattery.current, fmtKw(s.battery[i]))
    set(vLoad.current, fmtKw(s.load[i]))
    set(vSoc.current, fmtSoc(s.soc[i]))
    const label = pointLabel(s.resolution, s.t[i])
    set(vWhen.current, idx == null ? `Latest · ${label}` : label)
  }

  function chartData(s: HistorySeries): uPlot.AlignedData {
    const base: (number | null)[][] = [s.t, s.grid, s.battery, s.load]
    if (s.gridMax && s.gridMin && s.loadMax) base.push(s.gridMax, s.gridMin, s.loadMax)
    return base as uPlot.AlignedData
  }

  function emitView(u: uPlot) {
    const { min, max } = u.scales.x
    if (min == null || max == null) return
    const s = seriesRef.current
    const first = s.t[0] ?? min, last = s.t[s.t.length - 1] ?? max
    zoomed.current = min > first + 1 || max < last - 1
    viewCb.current?.(Math.floor(min), Math.ceil(max), zoomed.current)
  }

  function destroyCharts() {
    detachTouch.current.forEach((f) => f()); detachTouch.current = []
    powerChart.current?.destroy(); powerChart.current = null
    socChart.current?.destroy(); socChart.current = null
  }

  /** Drag one finger sideways to select a span; double-tap to reset. Vertical swipes scroll. */
  function attachTouchZoom(u: uPlot) {
    const over = u.over
    let startX = 0, startY = 0, left = 0, mode: 'idle' | 'undecided' | 'select' | 'scroll' = 'idle'
    let lastTap = 0
    const clear = () => u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false)
    const local = (t: Touch) => t.clientX - over.getBoundingClientRect().left
    const onStart = (e: TouchEvent) => {
      lastTouch.current = Date.now()
      if (e.touches.length !== 1) { mode = 'idle'; clear(); return }
      startX = local(e.touches[0]); startY = e.touches[0].clientY; mode = 'undecided'
    }
    const onMove = (e: TouchEvent) => {
      if (mode === 'idle' || mode === 'scroll' || e.touches.length !== 1) return
      const x = local(e.touches[0])
      const dx = x - startX, dy = e.touches[0].clientY - startY
      if (mode === 'undecided') {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        mode = Math.abs(dx) > Math.abs(dy) ? 'select' : 'scroll'
        if (mode === 'scroll') return
      }
      e.preventDefault()                            // a sideways drag selects; it doesn't pan the page
      const w = over.clientWidth
      const a = Math.max(0, Math.min(startX, x)), b = Math.min(w, Math.max(startX, x))
      left = a
      u.setSelect({ left: a, top: 0, width: b - a, height: over.clientHeight }, false)
    }
    const onEnd = () => {
      lastTouch.current = Date.now()
      if (mode === 'select') {
        const width = u.select.width
        clear()
        if (width > 10) {
          const min = u.posToVal(left, 'x'), max = u.posToVal(left + width, 'x')
          if (max - min > 60) u.setScale('x', { min, max })
        }
      } else if (mode === 'undecided') {
        const now = Date.now()
        if (now - lastTap < 350) { lastTap = 0; resetZoomNow() }
        else {
          lastTap = now
          // show the values at the tapped point (the charts' cursors are synced)
          u.setCursor({ left: startX, top: over.clientHeight / 2 })
        }
      }
      mode = 'idle'
    }
    const onCancel = () => { mode = 'idle'; clear() }
    over.addEventListener('touchstart', onStart, { passive: true })
    over.addEventListener('touchmove', onMove, { passive: false })
    over.addEventListener('touchend', onEnd)
    over.addEventListener('touchcancel', onCancel)
    detachTouch.current.push(() => {
      over.removeEventListener('touchstart', onStart)
      over.removeEventListener('touchmove', onMove)
      over.removeEventListener('touchend', onEnd)
      over.removeEventListener('touchcancel', onCancel)
    })
  }

  function resetZoomNow() {
    if (resetCb.current) { resetCb.current(); return }
    const s = seriesRef.current
    if (!powerChart.current || s.t.length === 0) return
    powerChart.current.setScale('x', { min: s.t[0], max: s.t[s.t.length - 1] })
  }

  function build(keep?: { min: number; max: number } | null) {
    const el = powerEl.current
    if (!el) return
    destroyCharts()
    const s = seriesRef.current
    const p = palette()
    const width = el.clientWidth || 600
    const narrow = isNarrow(width)
    const h = chartHeights(width, compact)
    const font = canvasFont(narrow ? 13 : 12)
    const yAxisSize = narrow ? 58 : 52
    const axisBase = { stroke: p.axis, font, grid: { stroke: p.gridline, width: 1 }, ticks: { stroke: p.gridline, width: 1, size: 4 } }
    const xAxis = {
      ...axisBase,
      space: narrow ? 64 : 50,                      // min px between time labels
      values: (u: uPlot, splits: number[]) => {
        const span = (u.scales.x.max ?? 0) - (u.scales.x.min ?? 0)
        const f = tickFormatter(span)
        return splits.map((v) => f(v))
      },
    }
    const line = (label: string, stroke: string) => ({
      label, stroke, width: 2, spanGaps: false, points: { show: false },
    })
    const ghost = (label: string) => ({ label, stroke: 'transparent', width: 0, spanGaps: false, points: { show: false } })
    const cursor: uPlot.Cursor = {
      sync: { key: syncKey },
      drag: compact ? { x: false, y: false } : { x: true, y: false, setScale: true },
      points: { size: 7, width: 2, fill: p.surface },
      bind: {
        // Browsers send a mouseleave straight after a tap; keep the tapped point's values showing.
        mouseleave: (_u, _t, handler) => (e) => {
          if (Date.now() - lastTouch.current < 1000) return null
          return handler(e)
        },
        // Zoom out through the page (it may need to drop finer detail first).
        dblclick: () => () => { if (!compact) resetZoomNow(); return null },
      },
    }
    const hooks = {
      setCursor: [(u: uPlot) => showValues(u.cursor.idx ?? null)],
      setScale: [(u: uPlot, key: string) => {
        if (key !== 'x' || syncing.current) return
        const { min, max } = u.scales.x
        syncing.current = true
        for (const other of [powerChart.current, socChart.current]) {
          if (other && other !== u && min != null && max != null) other.setScale('x', { min, max })
        }
        syncing.current = false
        emitView(u)
        showValues(null)
      }],
    }

    const powerSeries: uPlot.Series[] = [{}, line('Grid', p.grid), line('Battery', p.battery), line('Home use', p.load)]
    const bands: uPlot.Band[] = []
    if (s.gridMax) {
      powerSeries.push(ghost('Grid max'), ghost('Grid min'), ghost('Home use max'))
      bands.push({ series: [4, 5], fill: p.gridBand }, { series: [6, 3], fill: p.loadBand })
    }

    powerChart.current = new uPlot({
      width, height: h.power, tzDate, legend: { show: false }, cursor, bands,
      scales: {
        x: { time: true },
        y: { range: (_u, min, max) => [Math.floor(Math.min(min, 0) - 0.2), Math.ceil(Math.max(max, 0) + 0.2)] },
      },
      series: powerSeries,
      axes: [xAxis, { ...axisBase, size: yAxisSize, values: (_u, splits) => splits.map((v) => `${v} kW`) }],
      hooks: {
        ...hooks,
        draw: [(u: uPlot) => {
          // zero line: import/discharge above, export/charge below
          const y = Math.round(u.valToPos(0, 'y', true)) + 0.5
          const ctx = u.ctx
          ctx.save()
          ctx.strokeStyle = p.zero
          ctx.lineWidth = Math.max(1, devicePixelRatio)
          ctx.beginPath(); ctx.moveTo(u.bbox.left, y); ctx.lineTo(u.bbox.left + u.bbox.width, y); ctx.stroke()
          ctx.restore()
        }],
      },
    }, chartData(s), el)

    if (!compact && socEl.current) {
      socChart.current = new uPlot({
        width, height: h.soc, tzDate, legend: { show: false }, cursor,
        scales: { x: { time: true }, y: { range: [0, 100] } },
        series: [{}, line('State of charge', p.battery)],
        axes: [xAxis, { ...axisBase, size: yAxisSize, splits: () => [0, 50, 100], values: (_u, sp) => sp.map((v) => `${v}%`) }],
        hooks,
      }, [s.t, s.soc] as uPlot.AlignedData, socEl.current)
    }
    if (!compact) {
      attachTouchZoom(powerChart.current)
      if (socChart.current) attachTouchZoom(socChart.current)
    }
    built.current = { resolution: s.resolution, compact, narrow }
    shownKey.current = rangeKey
    zoomed.current = false
    if (keep) powerChart.current.setScale('x', keep)   // syncs the SoC chart and emits the view
    else emitView(powerChart.current)
    showValues(null)
  }

  // Build on first render and whenever the kind of data changes; otherwise
  // just swap the data in, keeping the zoom if the customer has zoomed.
  useEffect(() => {
    const b = built.current
    const newRange = shownKey.current !== rangeKey
    const keep = !newRange && hold ? { min: hold.min, max: hold.max } : null
    if (!powerChart.current || !b || b.resolution !== series.resolution || b.compact !== compact) {
      build(keep)
      return
    }
    shownKey.current = rangeKey
    if (newRange) zoomed.current = false
    if (keep) {
      powerChart.current.setData(chartData(series), false)
      socChart.current?.setData([series.t, series.soc] as uPlot.AlignedData, false)
      powerChart.current.setScale('x', keep)          // syncs the SoC chart and emits the view
      showValues(null)
      return
    }
    const keepZoom = zoomed.current
    powerChart.current.setData(chartData(series), !keepZoom)
    socChart.current?.setData([series.t, series.soc] as uPlot.AlignedData, !keepZoom)
    emitView(powerChart.current)
    showValues(null)
  }, [series, compact, rangeKey, hold?.min, hold?.max]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => destroyCharts(), [])

  // Follow the container width; rebuild on a theme change.
  useEffect(() => {
    const el = powerEl.current
    if (!el) return
    let timer = 0
    const ro = new ResizeObserver(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const w = el.clientWidth
        if (!powerChart.current || Math.abs(powerChart.current.width - w) < 2) return
        if (built.current && isNarrow(w) !== built.current.narrow) {
          // label size changes across the breakpoint (e.g. rotating a phone): rebuild, keep the zoom
          const { min, max } = powerChart.current.scales.x
          build(zoomed.current && min != null && max != null ? { min, max } : null)
          return
        }
        const h = chartHeights(w, compact)
        powerChart.current.setSize({ width: w, height: h.power })
        socChart.current?.setSize({ width: w, height: h.soc })
      }, 80)
    })
    ro.observe(el)
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onTheme = () => build()
    mq.addEventListener?.('change', onTheme)
    return () => { ro.disconnect(); window.clearTimeout(timer); mq.removeEventListener?.('change', onTheme) }
  }, [compact]) // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    resetZoom() { resetZoomNow() },
    async toPng(title: string, subtitle: string) {
      const c1 = powerChart.current?.ctx.canvas
      if (!c1) return null
      const c2 = socChart.current?.ctx.canvas
      const p = palette()
      const pr = window.devicePixelRatio || 1
      const pad = 16 * pr, gap = 12 * pr
      const width = c1.width + pad * 2
      const keys = [['Grid', p.grid], ['Battery', p.battery], ['Home use', p.load]] as const
      const small = canvasFont(12 * pr)
      // Measure first: on a narrow (portrait phone) image the legend gets its own line.
      const probe = document.createElement('canvas').getContext('2d')
      if (!probe) return null
      probe.font = small
      const keyW = (name: string) => 22 * pr + probe.measureText(name).width
      const legendW = keys.reduce((sum, [n]) => sum + keyW(n), 0) + 14 * pr * (keys.length - 1)
      const ownLine = pad + probe.measureText(subtitle).width + 24 * pr + legendW > width - pad
      const legendY = ownLine ? 66 * pr : 46 * pr
      const head = ownLine ? 84 * pr : 64 * pr
      const out = document.createElement('canvas')
      out.width = width
      out.height = head + c1.height + (c2 ? gap + c2.height : 0) + pad
      const x = out.getContext('2d')
      if (!x) return null
      x.fillStyle = p.surface; x.fillRect(0, 0, out.width, out.height)
      x.fillStyle = p.text; x.font = canvasFont(16 * pr, '600')
      x.fillText(title, pad, 26 * pr)
      x.fillStyle = p.axis; x.font = small
      x.fillText(subtitle, pad, 46 * pr)
      let lx = ownLine ? pad : width - pad - legendW   // left-aligned on its own line, right-aligned otherwise
      for (const [name, col] of keys) {
        x.fillStyle = col; x.fillRect(lx, legendY - 5 * pr, 16 * pr, 3 * pr)
        x.fillStyle = p.axis; x.fillText(name, lx + 22 * pr, legendY)
        lx += keyW(name) + 14 * pr
      }
      x.drawImage(c1, pad, head)
      if (c2) x.drawImage(c2, pad, head + c1.height + gap)
      return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'))
    },
  }))

  return (
    <div className={`history-charts${compact ? ' history-charts-compact' : ''}`}>
      <div className="history-legend-row">
        <ul className="history-legend" aria-label="Values">
          <li className="key-grid"><span className="history-key" /><span>Grid</span><strong ref={vGrid}>–</strong></li>
          <li className="key-battery"><span className="history-key" /><span>Battery</span><strong ref={vBattery}>–</strong></li>
          <li className="key-load"><span className="history-key" /><span>Home use</span><strong ref={vLoad}>–</strong></li>
          {compact && <li className="key-battery key-soc"><span>Charge</span><strong ref={vSoc}>–</strong></li>}
        </ul>
        <span className="history-when" ref={vWhen} />
      </div>
      <div className="history-chart" ref={powerEl}
        role="img" aria-label="Line chart of grid, battery and home power in kilowatts" />
      {!compact && (
        <>
          {aggregated && (
            <p className="history-note">Lines show the average for each {series.resolution === 'day' ? 'day' : series.resolution === 'hour' ? 'hour' : '5 minutes'}; shading shows the highest and lowest readings within it.</p>
          )}
          <div className="history-soc-head">
            <h3>Battery charge</h3>
            <ul className="history-legend">
              <li className="key-battery"><span className="history-key" /><span>State of charge</span><strong ref={vSoc}>–</strong></li>
            </ul>
          </div>
          <div className="history-chart" ref={socEl}
            role="img" aria-label="Line chart of battery state of charge in percent" />
        </>
      )}
    </div>
  )
})
